import { throttle, uniqBy } from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'
import { removeCountryName } from 'shared/helpers/formatters'

/* eslint-disable-next-line @typescript-eslint/naming-convention */
export type PlaceType = google.maps.places.AutocompletePrediction & { utc_offset_minutes?: number }

export interface AutocompleteOptionType {
  label: string
  value: string
}

export const formatOptions = (options: any) => {
  return options.map((option: any) => {
    const [value, city, state] = option.description.split(',')

    return {
      label: option.description,
      value: value?.trim(),
      city: city?.trim(),
      state: state?.trim() || '',
      place: option,
    }
  })
}

export const formatCityStateOptions = (options: any) => {
  return options.map((option: any) => {
    const [city, state] = option.description.split(',')

    return {
      label: `${city?.trim()}, ${state?.trim()}`,
      value: `${city?.trim()}, ${state?.trim()}`,
      place: option,
    }
  })
}

export function usePlacesAutoCompleteOptions(
  domElement: HTMLDivElement | null,
  value: string | number | undefined,
  searchTypes = 'address',
  optionsFormatter = formatOptions,
  includeZip = false
) {
  const autocompleteService = useRef(
    window.google ? new window.google.maps.places.AutocompleteService() : undefined
  )
  const placesService = useRef(
    domElement && window.google ? new window.google.maps.places.PlacesService(domElement!) : undefined
  )
  const [options, setOptions] = useState<AutocompleteOptionType[]>([])

  const fetch = useMemo(
    () =>
      throttle((request: any, callback: (results?: PlaceType[]) => void) => {
        ;(autocompleteService.current as any).getPlacePredictions(request, callback)
      }, 500),
    []
  )

  useEffect(() => {
    if (!value) setOptions([])
  }, [value])

  useEffect(() => {
    let active = true
    let searchedAddress: string | number | undefined = ''

    if (!autocompleteService.current && window && window.google) {
      autocompleteService.current = new google.maps.places.AutocompleteService()
    }

    if (!placesService.current && domElement && window.google) {
      placesService.current = new window.google.maps.places.PlacesService(domElement)
    }

    if (!autocompleteService.current || !placesService.current) {
      return undefined
    }

    searchedAddress = value
    const addressesRequest = {
      componentRestrictions: { country: 'us' },
      types: [searchTypes],
      input: value,
    }

    fetch(addressesRequest, async (results?: PlaceType[]) => {
      if (active) {
        if (results && addressesRequest.input === searchedAddress) {
          let filteredResults = results.map((res) => {
            return { ...res, description: removeCountryName(res.description) }
          })

          if (includeZip) {
            filteredResults = await Promise.all<PlaceType>(
              filteredResults.map((place) => {
                const request = {
                  placeId: place.place_id,
                  fields: ['address_component', 'utc_offset_minutes'],
                }

                return new Promise((resolve) => {
                  return placesService.current?.getDetails(request, (result) => {
                    if (result && result?.address_components && result?.address_components?.length > 0) {
                      const postCodeInfo = result.address_components.find(
                        (component) => component.types[0] === 'postal_code'
                      )

                      if (!postCodeInfo?.short_name) return resolve(place)

                      resolve({
                        ...place,
                        description: `${place.description}, ${postCodeInfo?.short_name}`,
                        utc_offset_minutes: result.utc_offset_minutes,
                      })
                    } else {
                      resolve(place)
                    }
                  })
                })
              })
            )
          }

          return setOptions(uniqBy(optionsFormatter(filteredResults), 'label') as any)
        }
      }
    })

    return () => {
      active = false
    }

    // domElement should not be added
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetch, value, searchTypes, optionsFormatter])

  return { options, placesService: placesService.current }
}
